import { toFormData } from 'axios';
import {
  PaginatedListingsResponse,
  MarketplacePreviewResponse,
  SocialData,
  FallbackListingsResponse,
  ListingsQueriesResponse,
  ListingsLomaResponse,
  AIRunResponse,
  MarketplacePreviewDataResponse,
} from '../types/types';

export type RequestParams = {
  /**
   * Relative URL for the endpoint
   */
  url: string;

  /**
   * Provide a body that will be serialized and sent to the endpoint in JSON format
   */
  body?: unknown;

  /**
   * Provide a pre-formatted body that will be sent to the endpoint in JSON format
   */
  formData?: FormData;

  /**
   * Indicates whether the body contains images and should be treated as form data instead
   */
  hasFiles?: boolean;

  /**
   * URL parameters to be appended to the URL
   */
  urlParams?: unknown;
};

const useRequest = () => {
  const baseUrl = process.env.NEXT_PUBLIC_API_URL || '';

  const makeRequest = async <T>(method: string, params: RequestParams) => {
    const { url, body, formData, hasFiles } = params;
    const completeUrl = `${baseUrl}${url}`;

    let parsedBody = body ?? formData;

    if (body && hasFiles) {
      parsedBody = toFormData(body as any);
    }
    const treatAsFormData = hasFiles || !!formData;

    const headers = new Headers();
    headers.append('Accept', 'application/json');
    if (!treatAsFormData) {
      headers.append('Content-Type', 'application/json');
    }

    const response = await fetch(completeUrl, {
      method,
      headers,
      mode: 'cors',
      credentials: 'include',
      body: treatAsFormData
        ? (parsedBody as FormData)
        : parsedBody
        ? JSON.stringify(parsedBody)
        : undefined,
    });

    const contentType = response.headers.get('content-type');
    let responseData;
    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }

    if (!response.ok) {
      const error = new Error(`HTTP error! status: ${response.status}`);
      // @ts-ignore
      error.status = response.status;
      // @ts-ignore
      error.response = { data: responseData };
      throw error;
    }

    return responseData as T;
  };

  const api = {
    ai: {
      createAIRunId: async (email: string, referralCode?: string | null) =>
        makeRequest<AIRunResponse>('POST', {
          url: `/ai_run/`,
          body: { email, ...(referralCode && { referral_code: referralCode }) },
        }),
      chooseAIRunId: async (email: string, marketplacePreviewDataId: string, emailRunId: number, upgrade?: boolean) =>
        makeRequest<AIRunResponse>('PATCH', {
          url: `/ai_run/`,
          body: { email, preview_data_id: marketplacePreviewDataId, email_run_id: emailRunId, ...(upgrade && { upgrade: true }) },
        }),
      getImportLogResults: async (run_ids: string[], key: string) =>
        makeRequest<{ results: unknown | unknown[] }>('POST', {
          url: `/get_import_log_results/`,
          body: { run_ids, key },
        }),
      createInitialMarketplaceDataFromUsername: async (username: string, network: string, email: string, emailRunId: number) =>
        makeRequest<string | SocialData>('POST', {
          url: `/generate_social_data/`,
          body: { username, network, email, email_run_id: emailRunId },
        }),
      createInitialMarketplaceData: async (body: object) =>
        makeRequest<string | MarketplacePreviewResponse>('POST', {
          url: `/generate_marketplace_data/`,
          body,
        }),
      createListingsStep: async (body: object) =>
        makeRequest<
          | string
          | ListingsQueriesResponse
          | ListingsLomaResponse
          | PaginatedListingsResponse
          | FallbackListingsResponse
        >('POST', {
          url: `/generate_listings_step/`,
          body,
        }),
      getPreviewImage: async (
        previewType: 'marketplace' | 'seller' | 'payouts',
        marketplace_preview_data_id: string,
        email_run_id: number | null,
      ) =>
        makeRequest<number | string>('GET', {
          url: `/generate_marketplace_preview_image/${previewType}/${marketplace_preview_data_id}/${email_run_id}/`,
        }),
      getListingPromoPreviewImage: async (marketplace_preview_data_id: string, email_run_id: number | null) =>
        makeRequest<string | { preview_images: string[] }>('GET', {
          url: `/generate_listing_promo_preview_image/${marketplace_preview_data_id}/${email_run_id}/`,
        }),
      getMarketplacePreview: async (marketplace_preview_data_id: string) =>
        makeRequest<MarketplacePreviewDataResponse>('GET', {
          url: `/get_marketplace_preview_data/${marketplace_preview_data_id}/`,
        }),
      statusChange: async (body: object) =>
        makeRequest<{ success: boolean, email_run_id: number }>('POST', {
          url: `/ai_status_change/`,
          body,
        }),
    },
    email: {
      sendVerificationCode: async (email: string, username: string, network: string, phSessionId: string) =>
        makeRequest<{ message: string; verificationUrl?: string }>('POST', {
          url: `/send-verification-code/`,
          body: { email, social_username: username, social_network: network, ph_session_id: phSessionId }, 
        }),
      verifyEmail: async (email: string, code: string) =>
        makeRequest<{ message: string, runs_left: number }>('POST', {
          url: `/verify-email/`,
          body: { email, verification_code: code },
        }),
      verifyEmailByToken: async (token: string) =>
        makeRequest<{ message: string; email: string, runs_left: number }>('POST', {
          url: `/verify-email-by-token/`,
          body: { token },
        }),
    },
    referral: {
      validateReferralCode: async (referralCode: string) =>
        makeRequest<{ valid: boolean; referrer_email?: string; error?: string }>('POST', {
          url: `/validate_referral_code/`,
          body: { referral_code: referralCode },
        }),
    },
  };

  return {
    api,
    post: <T>(params: RequestParams) => makeRequest<T>('POST', params),
  };
};

export default useRequest;
